import type { IntlShape } from "react-intl";
import { P, match } from "ts-pattern";
import { type RefinementCtx, type ZodErrorMap, ZodIssueCode } from "zod";
import { formatNumber } from "../..";

export function getErrorMap(intl: IntlShape, convert?: "mmToCm" | "cmToMm"): ZodErrorMap {
	const lang = intl.locale;
	const t = intl.formatMessage;

	const formatToUnit = (value: number, unit: "centimeter" | "millimeter" = "centimeter") => {
		const convertedValue = match(convert)
			.with("mmToCm", () => value / 10)
			.with("cmToMm", () => value * 10)
			.otherwise(() => value);

		if (unit === "centimeter") {
			return formatNumber({ lang, value: convertedValue, unit: "centimeter", style: "unit" });
		}

		return formatNumber({ lang, value: convertedValue, unit: "millimeter", style: "unit" });
	};

	return (error, ctx) => {
		return match(error)
			.with({ code: ZodIssueCode.invalid_string, validation: "url" }, () => ({
				message: t({ defaultMessage: "Url ongeldig" }),
			}))
			.with({ code: ZodIssueCode.invalid_string, validation: "email" }, () => ({
				message: t({ defaultMessage: "E-mailadres ongeldig" }),
			}))
			.with({ code: ZodIssueCode.invalid_string, validation: "date" }, () => ({
				message: t({ defaultMessage: "Datum ongeldig" }),
			}))
			.with({ code: ZodIssueCode.invalid_literal, expected: "1" }, () => ({
				message: t({ defaultMessage: "Veld verplicht" }),
			}))
			.with({ code: ZodIssueCode.invalid_enum_value }, () => ({
				message: t({ defaultMessage: "Veld verplicht" }),
			}))
			.with(
				{
					code: ZodIssueCode.invalid_type,
					received: P.union("undefined", "nan", "number", "string", "null", "object", "array"),
				},
				() => ({
					message: t({ defaultMessage: "Veld verplicht" }),
				}),
			)
			.with({ code: ZodIssueCode.too_small, type: "string", minimum: 1 }, () => ({
				message: t({ defaultMessage: "Veld verplicht" }),
			}))
			.with(
				{ code: ZodIssueCode.too_small, type: "string", minimum: P.number.gt(1) },
				({ minimum }) => ({
					message: t({ defaultMessage: "Moet minstens {count} tekens zijn" }, { count: minimum }),
				}),
			)
			.with(
				{
					code: ZodIssueCode.too_small,
					type: "array",
					minimum: 1,
					path: [...P.array(P.string), P.string.startsWith("image")],
				},
				() => ({
					message: t({ defaultMessage: "Voeg tenminste 1 bestand toe" }),
				}),
			)
			.with(
				{
					code: ZodIssueCode.too_small,
					type: "array",
					minimum: P.number.gt(1),
					path: [...P.array(P.string), P.string.startsWith("image")],
				},
				({ minimum }) => ({
					message: t(
						{ defaultMessage: "Voeg tenminste {amount} bestanden toe" },
						{ amount: minimum },
					),
				}),
			)
			.with({ code: ZodIssueCode.too_small, type: "array", minimum: 1 }, () => ({
				message: t({ defaultMessage: "Voeg tenminste 1 element toe" }),
			}))
			.with(
				{ code: ZodIssueCode.too_small, type: "array", minimum: P.number.gt(1) },
				({ minimum }) => ({
					message: t(
						{ defaultMessage: "Voeg tenminste {amount} elementen toe" },
						{ amount: minimum },
					),
				}),
			)
			.with({ code: ZodIssueCode.too_small, type: "number" }, (e) => {
				const minimum = formatToUnit(Number(e.minimum));

				if (!e.inclusive) {
					return {
						message: t(
							{
								defaultMessage: "De waarde van het veld moet groter zijn dan: {minimum}",
							},
							{ minimum },
						),
					};
				}

				return {
					message: t(
						{
							defaultMessage: "De waarde van het veld mag niet kleiner zijn dan: {minimum}",
						},
						{ minimum },
					),
				};
			})
			.with(
				{
					code: ZodIssueCode.custom,
					params: { type: "too_small_unit", unit: "mm", minimum: P.number },
				},
				{
					code: ZodIssueCode.custom,
					params: { type: "too_small_unit", unit: "cm", minimum: P.number },
				},
				(e) => {
					const minimum = formatToUnit(
						Number(e.params.minimum),
						match(e.params.unit)
							.with("mm", () => "millimeter" as const)
							.with("cm", () => "centimeter" as const)
							.exhaustive(),
					);

					return {
						message: t(
							{
								defaultMessage: "De waarde van het veld mag niet kleiner zijn dan: {minimum}",
							},
							{ minimum },
						),
					};
				},
			)
			.with({ code: ZodIssueCode.too_big, type: "string" }, (e) => ({
				message: t(
					{ defaultMessage: "Veld mag maximaal {value} tekens bevatten" },
					{ value: Number(e.maximum) },
				),
			}))
			.with({ code: ZodIssueCode.too_big, type: "number" }, (e) => {
				const maximum = formatToUnit(Number(e.maximum));

				if (!e.inclusive) {
					return {
						message: t(
							{
								defaultMessage: "De waarde van het veld moet kleiner zijn dan: {maximum}",
							},
							{ maximum },
						),
					};
				}

				return {
					message: t(
						{
							defaultMessage: "De waarde van het veld mag niet groter zijn dan: {maximum}",
						},
						{ maximum },
					),
				};
			})
			.with({ code: ZodIssueCode.custom, params: { type: "vat" } }, () => ({
				message: t({ defaultMessage: "BTW-nummer ongeldig" }),
			}))
			.with({ code: ZodIssueCode.custom, params: { type: "phone" } }, () => ({
				message: t({ defaultMessage: "Telefoonnummer ongeldig" }),
			}))
			.with({ code: ZodIssueCode.custom, params: { type: "word_count" } }, (error) => ({
				message: t(
					{
						defaultMessage:
							"Geef tenminste {count} woorden op (nog {remaining, plural, one {1 woord} other {{remaining} woorden}})",
					},
					{
						count: typeof error.params.count === "number" ? error.params.count : 0,
						remaining: typeof error.params.remaining === "number" ? error.params.remaining : 0,
					},
				),
			}))
			.with(
				{ code: ZodIssueCode.custom, params: { type: CustomErrorType.agreement_required } },
				() => ({
					message: t({ defaultMessage: "Ga akkoord met de voorwaarden" }),
				}),
			)
			.with(
				{ code: ZodIssueCode.custom, params: { type: CustomErrorType.character_count } },
				(error) => ({
					message: t(
						{
							defaultMessage:
								"Geef tenminste {count} karakters op (nog {remaining, plural, one {1 karakter} other {{remaining} karakters}})",
						},
						{
							count: typeof error.params.count === "number" ? error.params.count : 0,
							remaining: typeof error.params.remaining === "number" ? error.params.remaining : 0,
						},
					),
				}),
			)
			.with({ code: ZodIssueCode.custom, params: { type: "combined_too_big" } }, (e) => ({
				message: t(
					{
						defaultMessage:
							"De gecombineerde waarden van de velden {field_1} en {field_2} mag niet groter zijn dan {maxValue}. De totaalwaarde is nu {totalValue}.",
					},
					{
						field_1: String(e.params.field_1),
						field_2: String(e.params.field_2),
						maxValue: formatToUnit(Number(e.params.maxValue)),
						totalValue: formatToUnit(Number(e.params.totalValue)),
					},
				),
			}))
			.with({ code: ZodIssueCode.custom, params: { type: "combined_too_small" } }, (e) => ({
				message: t(
					{
						defaultMessage:
							"De gecombineerde waarden van de velden {field_1} en {field_2} mag niet kleiner zijn dan {minValue}. De totaalwaarde is nu {totalValue}.",
					},
					{
						field_1: String(e.params.field_1),
						field_2: String(e.params.field_2),
						minValue: formatToUnit(Number(e.params.minValue)),
						totalValue: formatToUnit(Number(e.params.totalValue)),
					},
				),
			}))
			.with({ code: ZodIssueCode.custom, params: { type: "two_too_small" } }, (e) => ({
				message: t(
					{
						defaultMessage: "{field_1} of {field_2} moet tenminste {minValue} zijn.",
					},
					{
						field_1: String(e.params.field_1),
						field_2: String(e.params.field_2),
						minValue: formatToUnit(Number(e.params.minValue)),
					},
				),
			}))
			.with({ code: ZodIssueCode.custom, params: { type: "belt_size" } }, (e) => ({
				message: t(
					{
						defaultMessage:
							"De waarde van {field_1} is maximaal {maxValue} wanneer de waarde van {field_2} {value} is.",
					},
					{
						field_1: intl.formatList(e.params.field_1 as string[], { type: "conjunction" }),
						field_2: intl.formatList(e.params.field_2 as string[], { type: "disjunction" }),
						maxValue: formatToUnit(Number(e.params.maxValue)),
						value: formatToUnit(Number(e.params.value)),
					},
				),
			}))
			.with({ code: ZodIssueCode.custom, params: { type: "belt_size_combined" } }, (e) => ({
				message: t(
					{
						defaultMessage:
							"De waarde van {field} is maximaal {maxValue} wanneer de gecombineerde waarden van {fields} {value} is.",
					},
					{
						field: String(e.params.field),
						fields: intl.formatList(e.params.fields as string[]),
						maxValue: formatToUnit(Number(e.params.maxValue)),
						value: formatToUnit(Number(e.params.value)),
					},
				),
			}))
			.with({ code: ZodIssueCode.custom, params: { type: "belt_size_combined_inverse" } }, (e) => ({
				message: t(
					{
						defaultMessage:
							"De gecombineerde waarden van {fields} is maximaal {maxValue} wanneer de waarde van {field} {value} is.",
					},
					{
						field: String(e.params.field),
						fields: intl.formatList(e.params.fields as string[]),
						maxValue: formatToUnit(Number(e.params.maxValue)),
						value: formatToUnit(Number(e.params.value)),
					},
				),
			}))
			.with({ code: ZodIssueCode.custom, params: { type: "between_values" } }, (e) => ({
				message: t(
					{
						defaultMessage:
							"De waarde van het veld mag niet kleiner zijn dan {minimum} en mag niet groter zijn dan: {maximum}",
					},
					{ minimum: Number(e.params.min), maximum: Number(e.params.max) },
				),
			}))
			.with({ code: ZodIssueCode.custom, params: { type: "required_corner" } }, (e) => ({
				message: t(
					{
						defaultMessage:
							"Geef bij tenminste {minimum, plural, one {1 hoek} other {{minimum} hoeken}} een waarde op",
					},
					{ minimum: Number(e.params.min) },
				),
			}))
			.with(
				{ code: ZodIssueCode.custom, params: { type: "difference_two_fields_minimum" } },
				(e) => {
					if (e.params.inclusive) {
						return {
							message: t(
								{
									defaultMessage:
										"De waarde van {field_1} moet minimaal gelijk zijn aan de waarde van {field_2}.",
								},
								{
									field_1: intl.formatList(e.params.field_1 as string[], { type: "conjunction" }),
									field_2: intl.formatList(e.params.field_2 as string[], { type: "disjunction" }),
								},
							),
						};
					}

					return {
						message: t(
							{
								defaultMessage:
									"De waarde van {field_1} moet minimaal {minimum} groter zijn dan de waarde van {field_2}.",
							},
							{
								field_1: intl.formatList(e.params.field_1 as string[], { type: "conjunction" }),
								field_2: intl.formatList(e.params.field_2 as string[], { type: "disjunction" }),
								minimum: formatToUnit(Number(e.params.minimum ?? 1)),
							},
						),
					};
				},
			)
			.with(
				{ code: ZodIssueCode.custom, params: { type: "difference_two_fields_maximum" } },
				(e) => {
					if (e.params.inclusive) {
						return {
							message: t(
								{
									defaultMessage:
										"De waarde van {field_1} mag maximaal gelijk zijn aan de waarde van {field_2}.",
								},
								{
									field_1: intl.formatList(e.params.field_1 as string[], { type: "conjunction" }),
									field_2: intl.formatList(e.params.field_2 as string[], { type: "disjunction" }),
								},
							),
						};
					}

					return {
						message: t(
							{
								defaultMessage:
									"De waarde van {field_1} moet minimaal {maximum} kleiner zijn dan de waarde van {field_2}.",
							},
							{
								field_1: intl.formatList(e.params.field_1 as string[], { type: "conjunction" }),
								field_2: intl.formatList(e.params.field_2 as string[], { type: "disjunction" }),
								maximum: formatToUnit(Number(e.params.maximum ?? 1)),
							},
						),
					};
				},
			)
			.otherwise(() => ({ message: ctx.defaultError })) as { message: string };
	};
}

export enum CustomErrorType {
	character_count = "character_count",
	agreement_required = "agreement_required",
}

export const minimumCharacterValidation =
	(count: number) => (value: string, context: RefinementCtx) => {
		const characterCount = value.trim().length;

		if (characterCount < count) {
			context.addIssue({
				code: ZodIssueCode.custom,
				params: {
					type: CustomErrorType.character_count,
					count: count,
					remaining: count - characterCount,
				},
			});
		}
	};
